import React from 'react';
// eslint-disable-next-line no-restricted-imports
import {
  ControllerFieldState,
  ControllerRenderProps,
  FieldValues,
  Path,
  useController,
  UseControllerProps,
  UseFormStateReturn,
} from 'react-hook-form';
import {
  EAmountInput,
  EAutocomplete,
  EAutocompleteProps,
  ECheckbox,
  ECheckboxGroup,
  ECheckboxGroupProps,
  ECheckboxProps,
  EDateInput,
  EDateInputProps,
  EFileUpload,
  EFileUploadProps,
  ELabelSelect,
  ELabelSelectProps,
  EPasswordField,
  EPasswordFieldProps,
  ERadioGroup,
  ERadioGroupProps,
  ESelect,
  ESelectProps,
  ESwitch,
  ESwitchProps,
  ETextArea,
  ETextAreaProps,
  ETextInput,
  ETextInputProps,
  ETimeInput,
  ETimeInputProps,
} from 'webapp-lib/Components';

import {useDateLanguageType} from './form.utils';
import {validDateRule} from './validations';

/* eslint-disable no-restricted-imports */
export {useController} from 'react-hook-form';
export type {
  ControllerFieldState,
  ControllerRenderProps,
  UseControllerProps,
} from 'react-hook-form';
/* eslint-enable no-restricted-imports */

export type FormFieldOmitProps =
  | 'onChange'
  | 'name'
  | 'value'
  | 'error'
  | 'errorMessage';

export type FormFieldOverrides = {
  customValue?: any;
  customOnChange?: (
    e: React.ChangeEvent<any>,
    originalEventHandler: React.ChangeEventHandler
  ) => void;
};

export type FormFieldProps<TFieldValues extends FieldValues> = Omit<
  ControllerRenderProps<TFieldValues, Path<TFieldValues>>,
  'ref'
> & {
  ref: undefined;
  error: boolean;
  errorMessage?: string;
  disabled: boolean;
};

export const convertPropsForField = <TFieldValues extends FieldValues>({
  formState,
  field,
  fieldState,
}: {
  formState: UseFormStateReturn<TFieldValues>;
  field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>;
  fieldState: ControllerFieldState;
}): FormFieldProps<TFieldValues> => ({
  ...field,
  ref: undefined,
  error: Boolean(fieldState.error),
  errorMessage: fieldState.error?.message,
  disabled: formState.isSubmitting,
});

export const combineProps = <T, TFieldValues extends FieldValues>(
  formProps: FormFieldProps<TFieldValues>,
  elementProps: T,
  overrides: FormFieldOverrides
): FormFieldProps<TFieldValues> & T => {
  const combinedProps = {
    ...formProps,
    ...elementProps,
  };

  if (overrides.customOnChange) {
    combinedProps.onChange = (event: React.ChangeEvent) =>
      overrides.customOnChange!(event, formProps.onChange);
  }

  if (overrides.customValue) {
    combinedProps.value = overrides.customValue;
  }
  return combinedProps;
};

const isRequired = (rules: UseControllerProps<any>['rules']) =>
  typeof rules?.required === 'object' && rules.required.value;

export const addAsteriskIfRequired = <LabelType,>(
  rules: UseControllerProps<any>['rules'],
  label: string | LabelType
): string | LabelType => {
  if (typeof label !== 'string') {
    return label;
  }

  // the object check is necessary to check that the rule is of type ValidationValueMessage
  if (isRequired(rules)) {
    return `${label}*`;
  }
  return label;
};

export type EFProps<TInputProps> = UseControllerProps<any> &
  Omit<TInputProps, FormFieldOmitProps> &
  FormFieldOverrides;

export type EFAutocompleteProps = EFProps<EAutocompleteProps>;

export const EFAutocomplete = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFAutocompleteProps): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  elementProps.required = isRequired(rules);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <EAutocomplete {...combinedProps} />;
};

export type EFSwitchProps = EFProps<ESwitchProps>;

export const EFSwitch = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFSwitchProps): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <ESwitch {...combinedProps} />;
};

export const EFRadioGroup = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customOnChange,
  ...elementProps
}: EFProps<ERadioGroupProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customOnChange,
  });
  return <ERadioGroup {...combinedProps} />;
};

export function EFCheckboxGroup({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<ECheckboxGroupProps>): React.ReactElement {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <ECheckboxGroup {...combinedProps} />;
}

export const EFCheckbox = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<ECheckboxProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <ECheckbox testId={name} {...combinedProps} />;
};

export const EFDateInput = ({
  name,
  rules: overrideRules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  lang: inputLang,
  ...elementProps
}: EFProps<EDateInputProps>): React.ReactElement => {
  const overrideValidateObj =
    overrideRules?.validate instanceof Function
      ? {unnamed: overrideRules.validate}
      : overrideRules?.validate ?? {};
  const rules = {
    ...overrideRules,
    validate: {
      ...(elementProps.disabled ? {} : validDateRule.validate),
      ...overrideValidateObj,
    },
  };
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  const lang = useDateLanguageType(inputLang);
  elementProps.label = addAsteriskIfRequired(
    overrideRules,
    elementProps?.label
  );
  const combinedProps = combineProps(
    formPropsForField,
    {...elementProps, lang},
    {
      customValue,
      customOnChange,
    }
  );
  return (
    <EDateInput
      actions={isRequired(overrideRules) ? ['today'] : ['clear', 'today']}
      {...combinedProps}
    />
  );
};

export const EFTimeInput = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  lang: inputLang,
  ...elementProps
}: EFProps<ETimeInputProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const lang = useDateLanguageType(inputLang);
  const combinedProps = combineProps(
    formPropsForField,
    {...elementProps, lang},
    {
      customValue,
      customOnChange,
    }
  );
  return <ETimeInput {...combinedProps} />;
};

export const EFLabelSelect = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<ELabelSelectProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <ELabelSelect {...combinedProps} />;
};

export const EFPasswordField = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<EPasswordFieldProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <EPasswordField {...combinedProps} />;
};

export const EFSelect = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<ESelectProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <ESelect {...combinedProps} />;
};

export const EFTextArea = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<ETextAreaProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <ETextArea {...combinedProps} />;
};

export const EFTextInput = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<ETextInputProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <ETextInput {...combinedProps} />;
};

export const EFAmountInput = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<ETextInputProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });
  return <EAmountInput {...combinedProps} />;
};

export const EFFileUpload = ({
  name,
  rules,
  shouldUnregister,
  defaultValue,
  control,
  customValue,
  customOnChange,
  ...elementProps
}: EFProps<EFileUploadProps>): React.ReactElement => {
  const formPropsForField = convertPropsForField(
    useController({name, rules, shouldUnregister, defaultValue, control})
  );
  elementProps.label = addAsteriskIfRequired(rules, elementProps?.label);
  const combinedProps = combineProps(formPropsForField, elementProps, {
    customValue,
    customOnChange,
  });

  return <EFileUpload {...combinedProps} />;
};
